import { ChangeDetectionStrategy, Component, LOCALE_ID, computed, inject, input } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { TranslationService } from '@/layout/service/translation.service';
import { RoomEnvironment, RoomUsageState, roomEnvironment } from '@/cloud/models/room-usage.models';
import { lightLevelIcon } from 'src/api/room-usage';

/**
 * A room's live environment: temperature, humidity and light level, each shown only when the
 * room's sensors measure it. Renders nothing for a room without environment readings. Updates
 * live with the room's usage (`locations:usage:{namespaceId}`).
 */
@Component({
    selector: 'app-room-environment',
    standalone: true,
    imports: [DecimalPipe],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @if (environment(); as env) {
            <ul class="room-environment" [class.room-environment--compact]="compact()" [class.room-environment--stale]="stale()" [attr.aria-label]="t()('roomEnvironment.title')">
                @if (env.temperatureC !== null) {
                    <li class="room-environment__item" data-value="temperature" [attr.title]="t()('roomEnvironment.temperature')">
                        <i class="fa fa-temperature-half" aria-hidden="true"></i>
                        <span class="room-environment__label">{{ t()('roomEnvironment.temperature') }}</span>
                        <span class="room-environment__value">{{ env.temperatureC | number: '1.1-1' }} °C</span>
                    </li>
                }
                @if (env.humidityPercent !== null) {
                    <li class="room-environment__item" data-value="humidity" [attr.title]="t()('roomEnvironment.humidity')">
                        <i class="fa fa-droplet" aria-hidden="true"></i>
                        <span class="room-environment__label">{{ t()('roomEnvironment.humidity') }}</span>
                        <span class="room-environment__value">{{ env.humidityPercent | number: '1.0-0' }} %</span>
                    </li>
                }
                @if (env.illuminanceLux !== null || env.lightLevel !== null) {
                    <li class="room-environment__item" data-value="light" [attr.title]="t()('roomEnvironment.light')">
                        <i [class]="lightIcon(env.lightLevel)" aria-hidden="true"></i>
                        <span class="room-environment__label">{{ t()('roomEnvironment.light') }}</span>
                        <span class="room-environment__value">{{ lightText(env) }}</span>
                    </li>
                }
            </ul>
        }
    `,
    styles: [
        `
            :host {
                display: contents;
            }
            .room-environment {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                gap: 0.375rem 1rem;
                margin: 0;
                padding: 0;
                list-style: none;
                font-size: 0.875rem;
                color: var(--text-color);
                font-variant-numeric: tabular-nums;
            }
            .room-environment--compact {
                gap: 0.25rem 0.75rem;
                font-size: 0.8125rem;
                color: var(--text-color-secondary);
            }
            .room-environment--stale {
                opacity: 0.6;
            }
            .room-environment__item {
                display: inline-flex;
                align-items: center;
                gap: 0.4rem;
                min-width: 0;
                white-space: nowrap;
            }
            .room-environment__item > i {
                color: var(--text-color-secondary);
            }
            /* Read by screen readers, hidden on screen: the icon says the same. */
            .room-environment__label {
                position: absolute;
                width: 1px;
                height: 1px;
                margin: -1px;
                padding: 0;
                overflow: hidden;
                clip: rect(0, 0, 0, 0);
                white-space: nowrap;
                border: 0;
            }
        `
    ]
})
export class RoomEnvironmentComponent {
    readonly t = inject(TranslationService).t;
    private readonly number = new DecimalPipe(inject(LOCALE_ID));

    readonly usage = input<RoomUsageState | null | undefined>(null);
    /** Smaller, secondary-coloured text for room cards. */
    readonly compact = input(false);
    /** The room's control system is offline, so these are its last known values. */
    readonly stale = input(false);

    readonly environment = computed(() => roomEnvironment(this.usage()));
    readonly lightIcon = lightLevelIcon;

    /** Lux and/or the sensors' dark / twilight / bright, e.g. `12 lx · Dark`. */
    lightText(env: RoomEnvironment): string {
        const parts: string[] = [];
        if (env.illuminanceLux !== null) parts.push(`${this.number.transform(env.illuminanceLux, '1.0-0')} lx`);
        if (env.lightLevel !== null) parts.push(this.t()('roomEnvironment.lightLevel.' + env.lightLevel));
        return parts.join(' · ');
    }
}
